/**
 * Attaches who the journey belongs to: the person, and the account they buy
 * under.
 *
 * Two things here are timing, not taste:
 *
 * - The plan is written only once it is known. `useEntitlements().plan` is
 *   `""` while its query is in flight and after an error, and reporting an
 *   empty plan would put a meaningless value on the person and the group that
 *   later breakdowns would have to explain away.
 * - The account group is set separately from the person. The plan belongs to
 *   the account; on the person alone, each colleague carries a private copy
 *   that goes stale the moment anyone upgrades.
 */
import { useEffect } from "react";
import { identifyUser, setAccountGroup } from "@/shared/analytics/analytics";
import { captureCompletedRegistration } from "@/shared/analytics/registration-analytics";
import { useAuth } from "@/shared/auth/auth.context";
import { useEntitlements } from "@/shared/entitlements/entitlements.context";
import type { IntegrationConsent } from "@/shared/integrations/integration.types";

export function useAnalyticsIdentity(
  workspaceId: string | null,
  consent: IntegrationConsent,
): void {
  const { isAuthenticated, user, account } = useAuth();
  const { plan, seats } = useEntitlements();

  const userId = user?.id ?? null;
  const email = user?.email ?? null;
  const displayName = user?.displayName ?? null;
  const accountRole = user?.accountRole ?? null;
  const accountId = account?.id ?? null;
  const accountName = account?.name ?? null;

  useEffect(() => {
    if (!isAuthenticated || !userId || !email || !accountId) return;

    if (consent === "granted") {
      identifyUser({
        userId,
        email,
        name: displayName ?? email,
        accountId,
        accountRole,
        workspaceId,
        // Re-runs and fills this in once entitlements resolve; identify merges
        // properties onto the same person.
        ...(plan ? { plan } : {}),
      });
    }
    // Always consume the one-shot registration marker. When consent is not
    // granted, captureEvent is inert, so granting later cannot replay it.
    captureCompletedRegistration();
  }, [
    consent,
    isAuthenticated,
    userId,
    email,
    displayName,
    accountRole,
    accountId,
    workspaceId,
    plan,
  ]);

  useEffect(() => {
    if (consent !== "granted" || !isAuthenticated || !accountId || !plan) return;

    setAccountGroup({
      accountId,
      name: accountName ?? accountId,
      plan,
      seatsUsed: seats.used,
      seatCap: seats.cap,
    });
  }, [consent, isAuthenticated, accountId, accountName, plan, seats.used, seats.cap]);
}
